import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "../../../apiClient";
import { refreshValues, refreshMultiParcel, getSupportedCads } from "../cadApi";
import RefreshValuesModal from "../components/RefreshValuesModal";

/**
 * HistorySection (v4)
 * - Displays rows NEWEST → OLDEST in the UI.
 * - “+ Add year” appends the next older year (appears at the bottom).
 * - Adds a 3rd column: % Δ YoY (auto-calculated).
 * - Per-table column format selection: currency | number | percent.
 * - Compare checkbox per table (max 2). If exactly 2 are checked, the PDF compares
 *   those two. Otherwise, the PDF prints only the first (CAD) table.
 *
 * Storage: persist history.tables ascending by year with {label, format, compare, rows}.
 */

const FORMAT_OPTS = [
  { key: "currency", label: "Currency ($)" },
  { key: "number",   label: "Number (1,234)" },
  { key: "percent",  label: "Percent (%)" },
];
const MAX_COMPARE = 2;

export default function HistorySection({ data, onChange, packetYear, packet, coverData }) {
  const initial = data?.history || {};
  const preservedPrint = initial.print;

  const pktYear =
    toYear(packetYear ?? data?.packetYear ?? data?.year ?? new Date().getFullYear()) ||
    new Date().getFullYear();

  // ---------- stable primitives for deps (lint-safe) ----------
  const dataCover   = data?.cover;
  const dataPI      = data?.property_info;
  const dataSummary = data?.summary;
  const packetId    = packet?.id;

  // ---- cover seeding ----
  const coverLikeFromProps = useMemo(() => {
    if (coverData) return coverData;
    if (dataCover) return dataCover;
    if (dataPI || dataSummary) return { property_info: dataPI, summary: dataSummary };
    return null;
  }, [coverData, dataCover, dataPI, dataSummary]);

  const [fetchedCover, setFetchedCover] = useState(null);
  const triedFetchRef = useRef(false);

  useEffect(() => {
    if (coverLikeFromProps || !packetId || triedFetchRef.current) return;
    triedFetchRef.current = true;
    (async () => {
      try {
        const res = await api.get(`/bapb/packets/${packetId}`);
        const sections = Array.isArray(res?.data?.sections) ? res.data.sections : [];
        let cover = null;
        for (const s of sections) {
          if ((s.type || "").toLowerCase() !== "cover") continue;
          if (!cover || (s.order_index ?? 0) >= (cover.order_index ?? 0)) cover = s;
        }
        if (cover?.data) setFetchedCover(cover.data);
      } catch {
        // silent
      }
    })();
  }, [packetId, coverLikeFromProps]);

  const coverSource = coverLikeFromProps || fetchedCover;

  // ---- seed tables (keep v2, migrate legacy, or seed from cover) ----
  const seedTables = useMemo(() => {
    if (Array.isArray(initial.tables) && initial.tables.length) {
      return sanitizeTables(initial.tables, pktYear);
    }

    if (Array.isArray(initial.rows) && initial.rows.length) {
      let migrated = initial.rows
        .map((r) => ({ year: toYear(r.year), value: onlyNum(r.value) }))
        .filter((r) => r.year !== null);
      migrated = ensureTwoYears(migrated, pktYear, coverSource);
      return [{
        id: uid(),
        label: "CAD Appraised Value",
        format: "currency",
        compare: false,
        rows: sortRowsAsc(migrated),
      }];
    }

    return [{
      id: uid(),
      label: "CAD Appraised Value",
      format: "currency",
      compare: false,
      rows: seedTwoYears(pktYear, coverSource),
    }];
  }, [initial, pktYear, coverSource]);

  const [tables, setTables] = useState(seedTables);

  // If cover arrives later, patch first table rows if missing
  useEffect(() => {
    if (!coverSource) return;
    setTables((prev) => {
      const next = prev.slice();
      const t0 = next[0] || { id: uid(), label: "CAD Appraised Value", format: "currency", compare: false, rows: [] };
      const existing = sortRowsAsc(t0.rows || []);
      // Only fill in values from cover for years that already exist — don't inject new year rows
      if (existing.length > 0) {
        const patched = existing.map((r) => {
          if (r.value) return r;
          const fromCover = onlyNum(getValueForYear(coverSource, r.year, pktYear) ?? "");
          return fromCover ? { ...r, value: fromCover } : r;
        });
        const changed = !rowsEqual(existing, patched);
        if (!changed) return prev;
        next[0] = { ...t0, rows: sortRowsAsc(patched) };
      } else {
        // No rows yet — seed with pktYear and pktYear-1
        const seeded = seedTwoYears(pktYear, coverSource);
        next[0] = { ...t0, rows: seeded };
      }
      return next;
    });
  }, [coverSource, pktYear]);

  // ---- persist to parent (also mirror legacy history.rows for PDF fallback) ----
  // NOTE: we intentionally do NOT depend on `onChange` here. PacketBuilder
  // creates a new callback on every render, and including it in the deps
  // would re-mark this section as dirty even when the data didn't change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const normalized = normalizeTables(tables); // stored ascending
    const legacyRows = (normalized[0]?.rows || [])
      .map((r) => ({ year: r.year, value: num(r.value) }))
      .filter((r) => r.year !== null);

    onChange?.({
      history: {
        tables: normalized,
        rows: legacyRows,
        print: preservedPrint,
      },
    });
  }, [tables, preservedPrint]);

  // ---- table ops ----
  function addTable() {
    setTables((prev) => [
      ...prev,
      { id: uid(), label: "New History Column", format: "number", compare: false, rows: [{ year: pktYear, value: "" }] },
    ]);
  }
  function removeTable(index) {
    if (index === 0) return; // locked
    setTables((prev) => prev.filter((_, i) => i !== index));
  }
  function renameTable(index, label) {
    if (index === 0) return; // locked
    setTables((prev) => {
      const next = prev.slice();
      next[index] = { ...next[index], label };
      return next;
    });
  }
  function changeFormat(index, fmt) {
    if (index === 0) return; // locked
    setTables((prev) => {
      const next = prev.slice();
      next[index] = { ...next[index], format: fmt };
      return next;
    });
  }
  function toggleCompare(index, checked) {
    setTables((prev) => {
      const next = prev.slice();
      const currCount = prev.filter((t) => !!t.compare).length;
      const willAdd = !!checked && !prev[index].compare;
      if (willAdd && currCount >= MAX_COMPARE) {
        return prev; // disallow a 3rd selection
      }
      next[index] = { ...next[index], compare: !!checked };
      return next;
    });
  }

  // ---- row ops ----
  function addYearRow(tableIndex) {
    setTables((prev) => {
      const next = prev.slice();
      const rows = sortRowsAsc(next[tableIndex].rows || []);
      const minYear = rows.length ? Math.min(...rows.map((r) => toYear(r.year) ?? pktYear)) : pktYear;
      const newYear = (Number.isFinite(minYear) ? minYear : pktYear) - 1;

      const prefill =
        tableIndex === 0
          ? onlyNum(getValueForYear(coverSource, newYear, pktYear) ?? "")
          : "";

      next[tableIndex] = {
        ...next[tableIndex],
        rows: sortRowsAsc([...rows, { year: newYear, value: prefill }]),
      };
      return next;
    });
  }

  function removeRow(tableIndex, rowIndex) {
    setTables((prev) => {
      const next = prev.slice();
      const rowsAsc = sortRowsAsc(next[tableIndex].rows || []);
      const rowsDesc = sortRowsDesc(rowsAsc);
      const target = rowsDesc[rowIndex];
      const remainingAsc = rowsAsc.filter(
        (r) => !(r.year === target.year && String(r.value ?? "") === String(target.value ?? ""))
      );
      next[tableIndex] = {
        ...next[tableIndex],
        rows: remainingAsc.length ? remainingAsc : [{ year: pktYear, value: "" }],
      };
      return next;
    });
  }

  function updateRow(tableIndex, rowIndex, key, value) {
    setTables((prev) => {
      const next = prev.slice();
      const rowsAsc = sortRowsAsc(next[tableIndex].rows || []);
      const rowsDesc = sortRowsDesc(rowsAsc);
      const target = rowsDesc[rowIndex];
      const ascIdx = rowsAsc.findIndex(
        (r) => r.year === target.year && String(r.value ?? "") === String(target.value ?? "")
      );
      const newVal = key === "year" ? onlyYear(value) : onlyNum(value);
      if (ascIdx >= 0) {
        rowsAsc[ascIdx] = { ...rowsAsc[ascIdx], [key]: key === "year" ? toYear(newVal) : newVal };
      }
      next[tableIndex] = { ...next[tableIndex], rows: sortRowsAsc(rowsAsc) };
      return next;
    });
  }

  // ---- UI ----
  const compareCount = (tables || []).filter((t) => !!t.compare).length;

  // ---- CAD Value Refresh for locked table (ti===0) ----
  const [cadLoading, setCadLoading] = useState(false);
  const [cadError, setCadError] = useState("");
  const [supportedCads, setSupportedCads] = useState([]);
  const [showCadInfo, setShowCadInfo] = useState(false);

  useEffect(() => {
    let alive = true;
    getSupportedCads()
      .then((list) => { if (alive) setSupportedCads(Array.isArray(list) ? list : []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const handleCadRefresh = useCallback(async () => {
    const pi = coverSource?.property_info || {};
    const accountStr = pi.account || pi.parcel || packet?.account_number;
    if (!accountStr) { setCadError("No account number found (check cover page)."); return; }

    const lockedTable = tables[0];
    if (!lockedTable) return;
    const rows = sortRowsAsc(lockedTable.rows || []);
    const years = rows.map((r) => toYear(r.year)).filter((y) => y != null);
    if (!years.length) { setCadError("No year rows to refresh."); return; }

    setCadLoading(true);
    setCadError("");
    try {
      // Fetch all years in parallel (handles comma-separated multi-parcel)
      const results = await Promise.all(
        years.map((yr) => refreshMultiParcel({ year: yr, accountStr, cover_meta: coverSource }))
      );

      const updates = {};
      const warnings = [];
      years.forEach((yr, i) => {
        const res = results[i];
        const row = res?.merged;
        if (row?.ok && row.market_value != null && row.market_value > 0) {
          updates[yr] = String(Math.round(row.market_value));
        } else {
          warnings.push(`${yr}: ${row?.error || "no data"}`);
        }
        if (res?.warnings?.length) warnings.push(...res.warnings);
      });

      if (Object.keys(updates).length) {
        setTables((prev) => {
          const next = prev.slice();
          const t0 = { ...next[0] };
          t0.rows = sortRowsAsc(
            (t0.rows || []).map((r) => {
              const yr = toYear(r.year);
              return yr != null && updates[yr] != null ? { ...r, value: updates[yr] } : r;
            })
          );
          next[0] = t0;
          return next;
        });
      }

      if (warnings.length) setCadError(warnings.join(" | "));
    } catch (err) {
      setCadError(err?.message || "CAD refresh failed.");
    } finally {
      setCadLoading(false);
    }
  }, [tables, coverSource, packet?.account_number]);

  return (
    <div className="space-y-4">
      {tables.map((t, ti) => {
        const isLocked = ti === 0;
        const displayLabel = isLocked ? "CAD Appraised Value" : t.label || "Value";
        const fmt = t.format || (isLocked ? "currency" : "number");
        const isChecked = !!t.compare;
        const disableCheck = !isChecked && compareCount >= MAX_COMPARE;

        // display descending (newest → oldest)
        const rowsAsc = sortRowsAsc(t.rows || []);
        const rowsDesc = sortRowsDesc(rowsAsc);
        const yoy = computeYoY(rowsDesc);

        return (
          <div key={t.id} className="rounded-xl border bg-white">
            <div className="flex items-center justify-between gap-3 px-3 py-2 border-b bg-slate-50">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Column:</span>
                  {isLocked ? (
                    <span className="px-2 py-1 text-sm rounded-md bg-slate-100 text-slate-700">
                      CAD Appraised Value (locked)
                    </span>
                  ) : (
                    <input
                      className="inp"
                      style={{ minWidth: 220 }}
                      value={t.label}
                      onChange={(e) => renameTable(ti, e.target.value)}
                      placeholder="e.g., Occupancy (SF)"
                    />
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Format:</span>
                  {isLocked ? (
                    <span className="chip">$ Currency</span>
                  ) : (
                    <select
                      className="sel"
                      value={fmt}
                      onChange={(e) => changeFormat(ti, e.target.value)}
                    >
                      {FORMAT_OPTS.map((o) => (
                        <option key={o.key} value={o.key}>{o.label}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Compare checkbox (max 2 total) */}
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="chk"
                      checked={isChecked}
                      onChange={(e) => toggleCompare(ti, e.target.checked)}
                      disabled={disableCheck}
                    />
                    <span className="text-sm text-slate-600">Compare</span>
                  </label>
                  <span className="text-xs text-slate-500">(max 2)</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button className="btn" onClick={() => addYearRow(ti)}>+ Add year</button>
                {!isLocked && (
                  <button className="btn-del" onClick={() => removeTable(ti)} title="Remove table">Remove table</button>
                )}
                {isLocked && (
                  <div className="inline-flex items-center gap-1 ml-auto">
                    <button
                      type="button"
                      disabled={cadLoading}
                      onClick={handleCadRefresh}
                      className={`rounded-md px-3 py-1.5 text-sm border ${
                        cadLoading
                          ? "border-slate-200 bg-slate-100 text-slate-400"
                          : "border-emerald-300 bg-emerald-50 text-emerald-800 hover:border-emerald-400"
                      }`}
                    >
                      {cadLoading ? "Refreshing\u2026" : "Refresh Values"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCadInfo(true)}
                      className="h-7 w-7 rounded-full border border-emerald-400 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 flex items-center justify-center text-sm"
                      title={`Pulls market values per row year\nSupported: ${(supportedCads || []).join(", ") || "loading..."}`}
                      aria-label="Refresh Values info"
                    >
                      &#8505;
                    </button>
                  </div>
                )}
              </div>
            </div>

            {isLocked && cadError && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5 mx-3 mb-2">
                {cadError}
              </div>
            )}

            <RefreshValuesModal
              open={showCadInfo}
              onClose={() => setShowCadInfo(false)}
              supportedCads={supportedCads}
              intro={
                <div className="space-y-3 text-sm text-slate-700">
                  <p>
                    <strong>Refresh Values</strong> pulls the{" "}
                    <strong>Total Market Value</strong> for each year row in the
                    table, using the account number from the cover page.
                  </p>
                  <p>
                    Each row's <strong>Year</strong> column determines which tax
                    year to fetch. Rows with no year are skipped.
                  </p>
                </div>
              }
            />

            <div className="overflow-auto">
              <table className="min-w-[720px] w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <Th>Year</Th>
                    <Th className="text-right">{displayLabel}</Th>
                    <Th className="text-right">% Δ YoY</Th>
                    <Th width="60"></Th>
                  </tr>
                </thead>
                <tbody>
                  {rowsDesc.map((r, ri) => (
                    <tr key={ri} className="border-t">
                      <Td width={140}>
                        <input
                          className="inp"
                          value={r.year ?? ""}
                          onChange={(e) => updateRow(ti, ri, "year", e.target.value)}
                          placeholder={String(pktYear)}
                        />
                      </Td>
                      <Td className="text-right">
                        <input
                          className="inp text-right"
                          value={r.value ?? ""}
                          onChange={(e) => updateRow(ti, ri, "value", e.target.value)}
                          placeholder={placeholderForFormat(fmt)}
                        />
                      </Td>
                      <Td className="text-right align-middle">
                        <span className={yoy[ri]?.cls || ""}>{yoy[ri]?.text ?? ""}</span>
                      </Td>
                      <Td className="text-right">
                        <button className="btn-del" onClick={() => removeRow(ti, ri)} title="Remove row">×</button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="px-3 py-2 text-xs text-slate-500 border-t">
              Display order: <strong>Newest → Oldest</strong>. “Add year” creates the next older year at the bottom.
            </div>
          </div>
        );
      })}

      <div className="flex items-center gap-2">
        <button className="btn" onClick={addTable}>+ Add another history table</button>
      </div>

      <style>{`
        .inp { width: 100%; border:1px solid #e5e7eb; background:#fff; border-radius:8px; padding:6px 8px; }
        .sel { border:1px solid #e5e7eb; background:#fff; padding:6px 8px; border-radius:8px; font-size:14px; }
        .chk { width: 16px; height: 16px; }
        .chip { border:1px solid #e5e7eb; background:#f8fafc; padding:4px 8px; border-radius:999px; font-size:12px; color:#475569; }
        .btn { border:1px solid #e5e7eb; background:#fff; padding:6px 10px; border-radius:10px; font-size:14px; }
        .btn:hover { border-color:#d1d5db; }
        .btn-del { border:1px solid #fecaca; background:#fef2f2; color:#b91c1c; padding:4px 10px; border-radius:8px; font-size:14px; }
        .pct-pos { color: #166534; } /* green-700 */
        .pct-neg { color: #991b1b; } /* red-800 */
        .pct-zero { color: #334155; } /* slate-700 */
      `}</style>
    </div>
  );
}

/* ---------------- helpers ---------------- */
function Th({ children, className = "", width }) {
  return <th className={`px-2 py-2 text-left ${className}`} style={width ? { width } : {}}>{children}</th>;
}
function Td({ children, className = "", width }) {
  return <td className={`px-2 py-1 ${className}`} style={width ? { width } : {}}>{children}</td>;
}

function uid() { return "t_" + Math.random().toString(36).slice(2, 9); }

function toYear(v) {
  if (v == null || v === "") return null;
  const n = Number(String(v).replace(/[^\d-]/g, ""));
  return Number.isFinite(n) ? n : null;
}
function onlyYear(s) { return String(s || "").replace(/[^\d-]/g, ""); }
function num(v) { if (v==null||v==="") return 0; const s=String(v).replace(/[$,%\s,]/g,""); const n=Number(s); return Number.isFinite(n)?n:0; }
function onlyNum(s){ return String(s||"").replace(/[^\d.-]/g,""); }
function isFiniteNum(v){ if(v==null||v==="") return false; const n=Number(String(v).replace(/[$,%\s,]/g,"")); return Number.isFinite(n); }

function sortRowsAsc(rows) {
  const withYears = (rows || [])
    .map((r) => ({ ...r, year: toYear(r.year) }))
    .filter((r) => r.year !== null);
  return withYears.sort((a, b) => a.year - b.year);
}
function sortRowsDesc(rows) {
  return sortRowsAsc(rows).slice().sort((a, b) => b.year - a.year);
}

function rowsEqual(a, b) {
  const A = sortRowsAsc(a || []), B = sortRowsAsc(b || []);
  if (A.length !== B.length) return false;
  for (let i=0;i<A.length;i++){
    if (A[i].year !== B[i].year) return false;
    if (String(A[i].value ?? "") !== String(B[i].value ?? "")) return false;
  }
  return true;
}
function normalizeTables(tables) {
  // Persist ascending (stable storage), include label + format + compare
  return (tables || []).map((t, i) => ({
    id: t.id || uid(),
    label: i === 0 ? "CAD Appraised Value" : String(t.label || "").trim() || "Value",
    format: (i === 0 ? (t.format || "currency") : (t.format || "number")),
    compare: !!t.compare,
    rows: sortRowsAsc(t.rows || []).map((r) => ({ year: r.year, value: r.value ?? "" })),
  }));
}
function sanitizeTables(tables, startYear) {
  const norm = normalizeTables(tables);
  return norm.length
    ? norm
    : [{ id: uid(), label: "CAD Appraised Value", format: "currency", compare: false, rows: [{ year: startYear, value: "" }] }];
}

/** Ensure we have two rows (pktYear & pktYear-1), filling values from Cover if available. */
function ensureTwoYears(rows, pktYear, coverLike) {
  const want = [pktYear - 1, pktYear];
  const existing = sortRowsAsc(rows || []);
  const mapped = new Map(existing.map((r) => [r.year, { ...r }]));

  for (const y of want) {
    const had = mapped.get(y);
    const fromCover = onlyNum(getValueForYear(coverLike, y, pktYear) ?? "");
    if (!had) {
      mapped.set(y, { year: y, value: fromCover || "" });
    } else if (!had.value && fromCover) {
      mapped.set(y, { ...had, value: fromCover });
    }
  }
  return Array.from(mapped.values());
}

/** Build the default two rows straight from Cover. */
function seedTwoYears(pktYear, coverLike) {
  const y0 = pktYear, y1 = pktYear - 1;
  const v0 = onlyNum(getValueForYear(coverLike, y0, pktYear) ?? "");
  const v1 = onlyNum(getValueForYear(coverLike, y1, pktYear) ?? "");
  return sortRowsAsc([{ year: y1, value: v1 }, { year: y0, value: v0 }]);
}

function getValueForYear(src, year, pktYear) {
  if (!src || !year) return undefined;

  // Many packets pass cover-like data nested; normalize a bit
  const cover = src.cover || src;
  const pi = cover?.property_info || src?.property_info || {};
  const summary = cover?.summary || src?.summary || {};

  // 1) Try by-year maps first
  const byYearCandidates = [
    cover?.cadAppraisedValueByYear,
    cover?.totalPropertyValueByYear,
    cover?.history?.cadAppraisedValue,
    cover?.valuesByYear?.cad,
    cover?.valuesByYear?.total,
    pi?.cadAppraisedValueByYear,
    pi?.totalPropertyValueByYear,
  ].filter(Boolean);

  for (const map of byYearCandidates) {
    const v = map?.[year] ?? map?.[String(year)];
    if (isFiniteNum(v)) return v;
  }

  // 2) Fall back to current / previous year fields commonly found on cover
  if (pktYear && year === pktYear) {
    const v = pi.current_year_value ?? pi.appraised_value ?? summary.current_appraised;
    if (isFiniteNum(v)) return v;
  }
  if (pktYear && year === pktYear - 1) {
    const v = pi.previous_year_value ?? pi.py_appraised_value ?? summary.py_appraised;
    if (isFiniteNum(v)) return v;
  }

  // 3) Final generic fallbacks
  for (const k of ["cadAppraisedValue", "totalPropertyValue", "oov", "current_value", "previous_value"]) {
    const v = pi?.[k] ?? cover?.[k];
    if (isFiniteNum(v)) return v;
  }

  return undefined;
}

/* ---- YoY helpers (display-only) ---- */
function computeYoY(rowsDesc /* newest → oldest */) {
  const out = [];
  for (let i = 0; i < rowsDesc.length; i++) {
    const curr = rowsDesc[i];
    const prev = rowsDesc[i + 1]; // prior year (older) in descending view
    if (!prev || !isFiniteNum(curr?.value) || !isFiniteNum(prev?.value) || Number(prev.value) === 0) {
      out.push({ text: "" });
      continue;
    }
    const pct = ((Number(curr.value) - Number(prev.value)) / Number(prev.value)) * 100;
    out.push({ text: fmtPct(pct), cls: pct > 0 ? "pct-pos" : pct < 0 ? "pct-neg" : "pct-zero" });
  }
  return out;
}
function fmtPct(p) {
  try {
    if (!Number.isFinite(p)) return "";
    const sign = p > 0 ? "+" : p < 0 ? "" : "";
    return `${sign}${p.toFixed(1)}%`;
  } catch { return ""; }
}

function placeholderForFormat(fmt) {
  if (fmt === "currency") return "$";
  if (fmt === "percent") return "85.0%";
  return "1,234";
}
